/**
 * ScanCapturePage — mobile-first camera capture page for shop scanning.
 *
 * Flow:
 *   1. Enter shop/section name
 *   2. Start camera preview (GPS sampling starts automatically)
 *   3. Record (auto-capture at 2fps)
 *   4. Confirm location (multi-sample average)
 *   5. Review frames
 *   6. Upload → track progress → done
 */

import { useState, useMemo, useRef, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useCamera } from '@hooks/useCamera'
import { useUploader } from '@hooks/useUploader'
import { useLocation } from '@hooks/useLocation'
import { useDeviceMotion } from '@hooks/useDeviceMotion'
import { useShopStore } from '../../stores/shopStore'
import {
  getPendingSessions,
  createSession,
  deleteSessionAndFrames,
  updateSession,
  generateSessionId,
  getStorageEstimate,
  getFramesBySession,
} from '@services/frameStore'
import type { ScanSession, PersistedFrame } from '@services/frameStore'
import type { ScanStatus } from '@app-types/scan'
import './ScanCapturePage.css'

type PageStep = 'resume-prompt' | 'setup' | 'camera' | 'confirm-location' | 'edit-location' | 'review' | 'upload'

export default function ScanCapturePage() {
  const { t } = useTranslation('scan')
  const [step, setStep] = useState<PageStep>('setup')
  const navigate = useNavigate()

  // Read shop/section from URL params (set by ShopsPage "Scan" button)
  const urlParams = new URLSearchParams(window.location.search)
  const [shopId, setShopId] = useState(urlParams.get('shopId') || '')
  const [sectionName, setSectionName] = useState(urlParams.get('sectionName') || '')
  const [sectionIdFromUrl, setSectionIdFromUrl] = useState(urlParams.get('sectionId') || '')
  const [newSectionName, setNewSectionName] = useState('')

  const [previewIndex, setPreviewIndex] = useState<number | null>(null)

  // User's shops from store
  const { shops, loading: shopsLoading, fetchShops, createShop } = useShopStore()
  const isNewShop = shopId === '__new_shop__'
  const selectedShop = isNewShop ? null : (shops.find((s) => s.id === shopId) || null)
  const [newShopName, setNewShopName] = useState('')

  // IndexedDB session for crash resilience
  const [scanSessionId, setScanSessionId] = useState<string>('')
  const [pendingSession, setPendingSession] = useState<ScanSession | null>(null)

  // ── Adaptive FPS controls ──
  const [manualFps, setManualFps] = useState(2)
  const [adaptiveEnabled, setAdaptiveEnabled] = useState(true)
  const deviceMotion = useDeviceMotion()

  const camera = useCamera({
    targetFps: manualFps,
    quality: 0.85,
    maxDimension: 800,
    scanSessionId,
    adaptiveFps: adaptiveEnabled,
    minFps: 1,
    maxFps: 8,
    similarityThreshold: 0.93,
    motionMagnitude: deviceMotion.motionMagnitude,
  })
  const uploader = useUploader()
  const location = useLocation()

  // State for manual location editing
  const [editLat, setEditLat] = useState('')
  const [editLng, setEditLng] = useState('')
  const [manualLocation, setManualLocation] = useState<{ lat: number; lng: number } | null>(null)

  // All pending sessions (for list display on setup step)
  const [allPendingSessions, setAllPendingSessions] = useState<ScanSession[]>([])
  // Frames loaded from IndexedDB for the resume-prompt preview
  const [resumeFrames, setResumeFrames] = useState<PersistedFrame[]>([])
  const [resumePreviewIndex, setResumePreviewIndex] = useState<number | null>(null)

  // ── Re-assign orphaned session state ──
  const [reassigningSessionId, setReassigningSessionId] = useState<string | null>(null)
  const [reassignShopId, setReassignShopId] = useState('')
  const [reassignSectionName, setReassignSectionName] = useState('')
  const [reassignIsNewSection, setReassignIsNewSection] = useState(false)

  // ── Fetch user's shops on mount ──
  useEffect(() => {
    fetchShops()
  }, [])

  // ── Capture initial location on mount ──
  useEffect(() => {
    location.captureInitialLocation()
  }, [])

  // ── Check for interrupted uploads on mount ──
  useEffect(() => {
    getPendingSessions().then((sessions) => {
      if (sessions.length > 0) {
        const sorted = sessions.sort((a, b) => b.createdAt - a.createdAt)
        setAllPendingSessions(sorted)
        // Show the most recent pending session
        setPendingSession(sorted[0])
        setStep('resume-prompt')
        // Load frames for preview
        getFramesBySession(sorted[0].id).then((frames) => {
          setResumeFrames(frames.sort((a, b) => a.frameIndex - b.frameIndex))
        }).catch(() => {})
      }
    }).catch((err) => {
      console.warn('[ScanCapturePage] Failed to check pending sessions:', err)
    })
  }, [])

  // Read picked location back from LocationPickerPage
  useEffect(() => {
    const pickedLat = urlParams.get('pickedLat')
    const pickedLng = urlParams.get('pickedLng')
    if (pickedLat && pickedLng) {
      const lat = parseFloat(pickedLat)
      const lng = parseFloat(pickedLng)
      if (!isNaN(lat) && !isNaN(lng)) {
        setEditLat(lat.toFixed(6))
        setEditLng(lng.toFixed(6))
        setManualLocation({ lat, lng })
        setStep('confirm-location')
      }
    }
  }, [])

  // Frame pairs (low-res + HD) for upload
  const framePairs = useMemo(() => camera.frames.map((f) => ({ blob: f.blob, hdBlob: f.hdBlob })), [camera.frames])

  // Start GPS sampling when camera step begins
  useEffect(() => {
    if (step === 'camera' && camera.isStreaming && !location.isActive) {
      location.startSampling()
    }
  }, [step, camera.isStreaming, location.isActive])

  // ── Handle shop selection ──
  const handleShopChange = (id: string) => {
    setShopId(id)
    // Reset section when shop changes
    setSectionName('')
    setSectionIdFromUrl('')
    setNewSectionName('')
    if (id !== '__new_shop__') setNewShopName('')
  }

  // ── Handle section selection ──
  const handleSectionChange = (sectionId: string) => {
    if (sectionId === '__new__') {
      setSectionIdFromUrl('')
      setSectionName('')
      return
    }
    const section = selectedShop?.sections.find((s) => s.sectionId === sectionId)
    if (section) {
      setSectionIdFromUrl(section.sectionId)
      setSectionName(section.name)
    }
  }

  // ── Setup Step ──
  const handleStartCamera = async () => {
    let resolvedShopId = shopId

    // If creating a new shop inline, do it now
    if (isNewShop) {
      if (!newShopName.trim()) return
      const created = await createShop(newShopName.trim())
      if (!created) {
        const err = useShopStore.getState().error
        if (err) alert(err)
        return
      }
      resolvedShopId = created.id
      setShopId(created.id)
      setNewShopName('')
    }

    // Validate: must have a real shop
    if (!resolvedShopId || resolvedShopId === '__new_shop__') return

    // Resolve section name: from selected section or new section input
    const finalSectionName = sectionName || newSectionName.trim()
    if (!finalSectionName) return

    // If user typed a new section name, save it now
    if (!sectionIdFromUrl && newSectionName.trim()) {
      setSectionName(newSectionName.trim())
    }

    // Create IndexedDB session for crash resilience
    const sessionId = generateSessionId()
    const finalSectionId = sectionIdFromUrl || ('sec_' + finalSectionName.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''))

    // Check storage availability
    const estimate = await getStorageEstimate()
    if (estimate && estimate.quota - estimate.usage < 200 * 1024 * 1024) {
      const usedMB = Math.round(estimate.usage / 1024 / 1024)
      const quotaMB = Math.round(estimate.quota / 1024 / 1024)
      alert(t('lowStorageWarning', { used: usedMB, quota: quotaMB }))
    }

    await createSession({
      id: sessionId,
      shopId: resolvedShopId,
      sectionId: finalSectionId,
      sectionName: finalSectionName,
      frameCount: 0,
      uploadedCount: 0,
      status: 'capturing',
      createdAt: Date.now(),
      updatedAt: Date.now(),
    })
    setScanSessionId(sessionId)

    // Switch to camera step FIRST so the <video> element mounts,
    // then startCamera will attach the stream via effect.
    setStep('camera')
    camera.startCamera()
  }

  // ── Camera Step ──
  const handleStartRecording = () => {
    camera.startCapture()
  }

  const handleStopRecording = () => {
    camera.stopCapture()
    location.stopSampling() // Stop GPS sampling and calculate average
    if (camera.frames.length > 0) {
      // Update session with final frame count and location
      if (scanSessionId) {
        const loc = manualLocation || location.averageLocation || null
        updateSession(scanSessionId, {
          frameCount: camera.frames.length,
          status: 'ready',
          location: loc,
        }).catch(() => {})
      }
      setStep('confirm-location')
    }
  }

  // ── Review Step ──
  const handleRetake = () => {
    camera.clearFrames()
    setStep('camera')
  }

  const handleUpload = async () => {
    setStep('upload')
    camera.stopCamera() // Free camera while uploading
    // Use manual location if edited, otherwise averaged location, otherwise null
    const finalLocation = manualLocation || location.averageLocation
    const finalSectionName = sectionName || newSectionName.trim()

    // Update session location before uploading
    if (scanSessionId) {
      await updateSession(scanSessionId, { location: finalLocation || null }).catch(() => {})
    }

    await uploader.upload(framePairs, shopId, finalSectionName, sectionIdFromUrl || undefined, finalLocation, scanSessionId || undefined)
  }

  // ── Upload Step ──
  const handleNewScan = () => {
    // Clean up IndexedDB session if exists
    if (scanSessionId) {
      deleteSessionAndFrames(scanSessionId).catch(() => {})
      setScanSessionId('')
    }
    uploader.reset()
    camera.clearFrames()
    location.clearSamples()
    setManualLocation(null)
    setPendingSession(null)
    setStep('setup')
  }

  // ── Resume handlers ──
  const handleResumeUpload = async () => {
    if (!pendingSession) return
    setStep('upload')
    await uploader.resume(pendingSession)
  }

  const handleRetryUpload = async () => {
    // Retry the current upload: re-read the session from IndexedDB and resume
    if (scanSessionId) {
      const sessions = await getPendingSessions()
      const current = sessions.find((s) => s.id === scanSessionId)
      if (current) {
        uploader.reset()
        setStep('upload')
        await uploader.resume(current)
        return
      }
    }
    // If no active session but there's a pending one, use that
    if (pendingSession) {
      uploader.reset()
      setStep('upload')
      await uploader.resume(pendingSession)
    }
  }

  const handleDiscardPending = async () => {
    if (!pendingSession) return
    await deleteSessionAndFrames(pendingSession.id).catch(() => {})
    // Remove from allPendingSessions
    const remaining = allPendingSessions.filter((s) => s.id !== pendingSession.id)
    setAllPendingSessions(remaining)
    if (remaining.length > 0) {
      setPendingSession(remaining[0])
      getFramesBySession(remaining[0].id).then((frames) => {
        setResumeFrames(frames.sort((a, b) => a.frameIndex - b.frameIndex))
      }).catch(() => {})
    } else {
      setPendingSession(null)
      setResumeFrames([])
      setStep('setup')
    }
  }

  // Re-assign an orphaned session to a different shop
  const handleReassignSave = async (session: ScanSession) => {
    const targetShop = shops.find((s) => s.id === reassignShopId)
    if (!targetShop || !reassignSectionName.trim()) return

    const newSectionId = 'sec_' + reassignSectionName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')
    await updateSession(session.id, {
      shopId: reassignShopId,
      sectionId: newSectionId,
      sectionName: reassignSectionName.trim(),
    })

    // Update local state so the UI reflects the change immediately
    setAllPendingSessions((prev) =>
      prev.map((s) => s.id === session.id
        ? { ...s, shopId: reassignShopId, sectionId: newSectionId, sectionName: reassignSectionName.trim() }
        : s
      )
    )
    setReassigningSessionId(null)
    setReassignShopId('')
    setReassignSectionName('')
    setReassignIsNewSection(false)

    // Auto-resume the now-valid session
    const updated = { ...session, shopId: reassignShopId, sectionId: newSectionId, sectionName: reassignSectionName.trim() }
    handleSelectPendingSession(updated)
  }

  // Switch to a different pending session
  const handleSelectPendingSession = (session: ScanSession) => {
    setPendingSession(session)
    setResumeFrames([])
    getFramesBySession(session.id).then((frames) => {
      setResumeFrames(frames.sort((a, b) => a.frameIndex - b.frameIndex))
    }).catch(() => {})
    setStep('resume-prompt')
  }

  // ── Location Confirmation handlers ──
  const handleConfirmLocation = () => {
    setStep('review')
  }

  const handleSkipLocation = () => {
    location.clearSamples()
    setManualLocation(null)
    setStep('review')
  }

  const handleEditLocation = () => {
    const loc = manualLocation || location.averageLocation
    if (loc) {
      setEditLat(loc.lat.toFixed(6))
      setEditLng(loc.lng.toFixed(6))
    } else {
      setEditLat('')
      setEditLng('')
    }
    setStep('edit-location')
  }

  const handleSaveEditedLocation = () => {
    const lat = parseFloat(editLat)
    const lng = parseFloat(editLng)
    if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      setManualLocation({ lat, lng })
      setStep('confirm-location')
    } else {
      alert(t('invalidCoordinates'))
    }
  }

  const handleUseCurrentLocation = () => {
    // Navigate to the map picker
    const params = new URLSearchParams({ returnTo: '/scan' })
    if (shopId) params.set('shopId', shopId)
    if (sectionName) params.set('sectionName', sectionName)
    if (sectionIdFromUrl) params.set('sectionId', sectionIdFromUrl)
    const loc = manualLocation || location.averageLocation
    if (loc) {
      params.set('lat', loc.lat.toString())
      params.set('lng', loc.lng.toString())
    }
    navigate(`/location-picker?${params.toString()}`)
  }

  return (
    <div className="scan-page">
      <header className="scan-header">
        <h1>{t('dukkanScan')}</h1>
        {step !== 'setup' && (
          <button className="scan-btn-text" onClick={() => { camera.stopCamera(); camera.stopCapture(); setStep('setup') }}>
            {t('common:back')}
          </button>
        )}
      </header>

      {/* ── RESUME PROMPT ── */}
      {step === 'resume-prompt' && pendingSession && (
        <div className="scan-step scan-resume">
          <div className="scan-resume-card">
            <h2>{t('uploadInterrupted')}</h2>
            <p className="scan-resume-detail">
              <strong>{pendingSession.sectionName}</strong> &middot; {pendingSession.shopId}
            </p>
            <p className="scan-resume-detail">
              {t('framesCaptured', { count: pendingSession.frameCount })}
              {pendingSession.uploadedCount > 0 && (
                <> &middot; {t('alreadyUploaded', { count: pendingSession.uploadedCount })}</>
              )}
            </p>
            {pendingSession.location && (
              <p className="scan-resume-detail">
                📍 {pendingSession.location.lat.toFixed(6)}, {pendingSession.location.lng.toFixed(6)}
              </p>
            )}
            <p className="scan-resume-time">
              {t('startedAt', { time: new Date(pendingSession.createdAt).toLocaleString() })}
            </p>

            {/* ── Frame thumbnail strip ── */}
            {resumeFrames.length > 0 && (
              <div className="scan-resume-frames">
                <p className="scan-resume-frames-label">{t('capturedFrames')}</p>
                <div className="scan-resume-frame-strip">
                  {resumeFrames.slice(0, 20).map((frame, i) => (
                    <img
                      key={frame.id}
                      src={URL.createObjectURL(frame.blob)}
                      alt={t('frameAlt', { number: i + 1 })}
                      className="scan-resume-thumb"
                      onClick={() => setResumePreviewIndex(i)}
                    />
                  ))}
                  {resumeFrames.length > 20 && (
                    <span className="scan-resume-more">+{resumeFrames.length - 20}</span>
                  )}
                </div>
              </div>
            )}

            <div className="scan-resume-actions">
              <button className="scan-btn-primary" onClick={handleResumeUpload}>
                {t('resumeUpload')}
              </button>
              <button className="scan-btn-secondary" onClick={handleDiscardPending}>
                {t('discardAndStartFresh')}
              </button>
            </div>

            {/* Other pending sessions */}
            {allPendingSessions.length > 1 && (
              <div className="scan-resume-others">
                <p className="scan-resume-others-label">{t('otherPendingScans')}</p>
                {allPendingSessions.filter((s) => s.id !== pendingSession.id).map((s) => (
                  <button
                    key={s.id}
                    className="scan-resume-other-item"
                    onClick={() => handleSelectPendingSession(s)}
                  >
                    <strong>{s.sectionName}</strong> &middot; {s.shopId} &middot; {s.frameCount} frames
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Frame Preview Overlay for resume */}
          {resumePreviewIndex !== null && resumePreviewIndex < resumeFrames.length && (
            <FramePreview
              frames={resumeFrames.map((f) => ({ blob: f.blob, index: f.frameIndex }))}
              currentIndex={resumePreviewIndex}
              onNavigate={setResumePreviewIndex}
              onDelete={() => {}} // No deletion in resume mode
              onClose={() => setResumePreviewIndex(null)}
              hideDelete
            />
          )}
        </div>
      )}

      {/* ── SETUP ── */}
      {step === 'setup' && (
        <div className="scan-step scan-setup">
          {/* ── Shop selector ── */}
          <div className="scan-field">
            <label>{t('shopId')}</label>
            {shopsLoading ? (
              <div className="scan-field-loading"><span className="scan-spinner-small" /> {t('common:loading')}</div>
            ) : (
              <>
                <select
                  className="scan-select"
                  value={shopId}
                  onChange={(e) => handleShopChange(e.target.value)}
                >
                  <option value="">{t('selectShop')}</option>
                  <option value="__new_shop__">+ {t('newShop')}</option>
                  {shops.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                {isNewShop && (
                  <input
                    type="text"
                    className="scan-input-new-section"
                    placeholder={t('shopNamePlaceholder')}
                    value={newShopName}
                    onChange={(e) => setNewShopName(e.target.value)}
                    autoFocus
                  />
                )}
              </>
            )}
          </div>

          {/* ── Section selector (only when a real shop is selected or creating new shop) ── */}
          {(selectedShop || isNewShop) && (
            <div className="scan-field">
              <label>{t('sectionName')}</label>
              {selectedShop && selectedShop.sections.length > 0 ? (
                <>
                  <select
                    className="scan-select"
                    value={sectionIdFromUrl || (newSectionName ? '__new__' : '')}
                    onChange={(e) => handleSectionChange(e.target.value)}
                  >
                    <option value="">{t('selectSection')}</option>
                    {selectedShop.sections.map((sec) => (
                      <option key={sec.sectionId} value={sec.sectionId}>{sec.name}</option>
                    ))}
                    <option value="__new__">+ {t('newSection')}</option>
                  </select>
                  {/* New section name input when "New Section" is chosen */}
                  {!sectionIdFromUrl && (
                    <input
                      type="text"
                      className="scan-input-new-section"
                      placeholder={t('sectionNamePlaceholder')}
                      value={newSectionName}
                      onChange={(e) => setNewSectionName(e.target.value)}
                    />
                  )}
                </>
              ) : (
                <input
                  type="text"
                  placeholder={t('sectionNamePlaceholder')}
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                />
              )}
            </div>
          )}

          {/* ── Location indicator ── */}
          <div className="scan-setup-location">
            {location.isLocating ? (
              <span className="scan-setup-location-status scan-locating">
                <span className="scan-spinner-small" /> {t('locating')}
              </span>
            ) : location.initialLocation ? (
              <span className="scan-setup-location-status scan-located">
                📍 {location.initialLocation.lat.toFixed(6)}, {location.initialLocation.lng.toFixed(6)}
                <span className="scan-setup-location-accuracy">(±{Math.round(location.initialLocation.accuracy)}m)</span>
              </span>
            ) : (
              <span className="scan-setup-location-status scan-no-location">
                📍 {t('locationNotDetected')}
                <button className="scan-btn-text scan-btn-locate" onClick={() => location.captureInitialLocation()}>
                  {t('retry')}
                </button>
              </span>
            )}
          </div>

          <button
            className="scan-btn-primary"
            disabled={(!shopId || (!selectedShop && !isNewShop)) || !(sectionName || newSectionName.trim()) || (isNewShop && !newShopName.trim())}
            onClick={handleStartCamera}
          >
            {t('startCamera')}
          </button>

          {/* ── Pending sessions list ── */}
          {allPendingSessions.length > 0 && (
            <div className="scan-pending-list">
              <h3>{t('pendingScans')}</h3>
              {allPendingSessions.map((s) => {
                const pendShop = shops.find((sh) => sh.id === s.shopId)
                return (
                  <div key={s.id} className="scan-pending-item">
                    <div className="scan-pending-item-info">
                      <strong>{s.sectionName}</strong> &middot; {pendShop ? pendShop.name : s.shopId}
                      {!pendShop && <span className="scan-pending-item-warning"> ⚠️ {t('shopNotFound')}</span>}
                      <br />
                      <span className="scan-pending-item-meta">
                        {s.frameCount} frames &middot; {new Date(s.createdAt).toLocaleDateString()}
                        {s.uploadedCount > 0 && <> &middot; {s.uploadedCount} uploaded</>}
                      </span>
                    </div>
                    <div className="scan-pending-item-actions">
                      {pendShop ? (
                        <button className="scan-btn-text" onClick={() => handleSelectPendingSession(s)}>
                          {t('resumeUpload')}
                        </button>
                      ) : (
                        <>
                          <button className="scan-btn-text" onClick={() => {
                            setReassigningSessionId(s.id)
                            setReassignShopId('')
                            setReassignSectionName(s.sectionName || '')
                            setReassignIsNewSection(false)
                          }}>
                            {t('reassign')}
                          </button>
                          <button className="scan-btn-text scan-btn-discard" onClick={async () => {
                            await deleteSessionAndFrames(s.id).catch(() => {})
                            setAllPendingSessions((prev) => prev.filter((p) => p.id !== s.id))
                          }}>
                            {t('discard')}
                          </button>
                        </>
                      )}
                    </div>

                    {/* ── Inline re-assign form ── */}
                    {reassigningSessionId === s.id && (
                      <div className="scan-reassign-form">
                        <select
                          className="scan-select"
                          value={reassignShopId}
                          onChange={(e) => setReassignShopId(e.target.value)}
                        >
                          <option value="">{t('selectShop')}</option>
                          {shops.map((sh) => (
                            <option key={sh.id} value={sh.id}>{sh.name}</option>
                          ))}
                        </select>
                        {reassignShopId && (() => {
                          const rShop = shops.find((sh) => sh.id === reassignShopId)
                          return rShop && rShop.sections.length > 0 ? (
                            <select
                              className="scan-select"
                              value={reassignIsNewSection ? '__new__' : reassignSectionName}
                              onChange={(e) => {
                                if (e.target.value === '__new__') {
                                  setReassignIsNewSection(true)
                                  setReassignSectionName('')
                                } else {
                                  setReassignIsNewSection(false)
                                  setReassignSectionName(e.target.value)
                                }
                              }}
                            >
                              <option value="">{t('selectSection')}</option>
                              {rShop.sections.map((sec) => (
                                <option key={sec.sectionId} value={sec.name}>{sec.name}</option>
                              ))}
                              <option value="__new__">+ {t('newSection')}</option>
                            </select>
                          ) : null
                        })()}
                        {reassignShopId && (reassignIsNewSection || !shops.find((sh) => sh.id === reassignShopId)?.sections.length) && (
                          <input
                            type="text"
                            className="scan-input-new-section"
                            placeholder={t('sectionNamePlaceholder')}
                            value={reassignSectionName}
                            onChange={(e) => setReassignSectionName(e.target.value)}
                          />
                        )}
                        <div className="scan-reassign-actions">
                          <button
                            className="scan-btn-primary scan-btn-small"
                            disabled={!reassignShopId || !reassignSectionName.trim()}
                            onClick={() => handleReassignSave(s)}
                          >
                            {t('saveAndResume')}
                          </button>
                          <button className="scan-btn-text" onClick={() => setReassigningSessionId(null)}>
                            {t('cancel')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ── CAMERA ── */}
      {step === 'camera' && (
        <div className="scan-step scan-camera">
          <div className="scan-video-container">
            <video ref={camera.videoRef} autoPlay playsInline muted className="scan-video" />
            <canvas ref={camera.canvasRef} style={{ display: 'none' }} />
            {camera.isCapturing && (
              <div className="scan-recording-badge">
                <span className="scan-dot" /> {t('rec')} &middot; {t('framesCount', { count: camera.frames.length })}
                {adaptiveEnabled && camera.adaptiveMetrics.framesSkipped > 0 && (
                  <span className="scan-skipped-badge"> &middot; {t('framesSkipped', { count: camera.adaptiveMetrics.framesSkipped })}</span>
                )}
                {scanSessionId && <span className="scan-saved-badge"> &middot; {t('saved')}</span>}
              </div>
            )}
          </div>

          {camera.error && <div className="scan-error">{camera.error}</div>}

          {/* ── FPS Slider ── */}
          <div className="scan-fps-controls">
            <div className="scan-fps-slider-row">
              <label className="scan-fps-label">{t('fpsLabel', { fps: manualFps })}</label>
              <input
                type="range"
                min={1}
                max={8}
                step={1}
                value={manualFps}
                onChange={(e) => setManualFps(Number(e.target.value))}
                className="scan-fps-slider"
              />
              {adaptiveEnabled && camera.isCapturing && (
                <span className="scan-fps-effective">{t('actualFps', { fps: camera.adaptiveMetrics.currentFps })}</span>
              )}
            </div>
            <div className="scan-fps-toggle-row">
              <label className="scan-fps-toggle">
                <input
                  type="checkbox"
                  checked={adaptiveEnabled}
                  onChange={(e) => setAdaptiveEnabled(e.target.checked)}
                />
                <span>{t('adaptive')}</span>
              </label>
              {adaptiveEnabled && !deviceMotion.isAvailable && (
                <button
                  className="scan-btn-text scan-btn-motion-perm"
                  onClick={() => deviceMotion.requestPermission()}
                >
                  {t('enableMotionSensor')}
                </button>
              )}
            </div>
          </div>

          <div className="scan-controls">
            {!camera.isCapturing ? (
              <button className="scan-btn-record" onClick={handleStartRecording} disabled={!camera.isStreaming}>
                <span className="scan-record-circle" />
                {t('startRecording')}
              </button>
            ) : (
              <button className="scan-btn-stop" onClick={handleStopRecording}>
                <span className="scan-stop-square" />
                {t('stopCount', { count: camera.frames.length })}
              </button>
            )}
          </div>

          <div className="scan-tip">
            {t('cameraTip')}
          </div>
        </div>
      )}

      {/* ── CONFIRM LOCATION ── */}
      {step === 'confirm-location' && (
        <div className="scan-step scan-location-confirm">
          <h2>{t('captureComplete')}</h2>
          
          {location.averageLocation || manualLocation ? (
            <div className="scan-location-info">
              <h3>{t('locationCaptured')}</h3>
              {manualLocation ? (
                <>
                  <p className="scan-location-coords">
                    {manualLocation.lat.toFixed(6)}, {manualLocation.lng.toFixed(6)}
                  </p>
                  <p className="scan-location-meta">({t('manuallyEntered')})</p>
                </>
              ) : location.averageLocation ? (
                <>
                  <p className="scan-location-coords">
                    {location.averageLocation.lat.toFixed(6)}, {location.averageLocation.lng.toFixed(6)}
                  </p>
                  <p className="scan-location-meta">
                    {t('locationAccuracy', { samples: location.averageLocation.sampleCount, accuracy: Math.round(location.averageLocation.accuracy) })}
                  </p>
                </>
              ) : null}
              <a
                href={`https://maps.google.com/?q=${(manualLocation || location.averageLocation)!.lat},${(manualLocation || location.averageLocation)!.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="scan-location-map-link"
              >
                {t('viewOnGoogleMaps')}
              </a>
            </div>
          ) : (
            <div className="scan-location-info">
              <p className="scan-location-warning">{t('locationNotAvailable')}</p>
              <p className="scan-location-meta">
                {t('locationUnavailableHint')}
              </p>
            </div>
          )}

          <p className="scan-location-prompt">{t('isThisCorrect')}</p>

          <div className="scan-location-actions">
            <button className="scan-btn-primary" onClick={handleConfirmLocation}>
              ✓ {t('common:continue')}
            </button>
            {(location.averageLocation || manualLocation) && (
              <button className="scan-btn-secondary" onClick={handleEditLocation}>
                {t('editLocation')}
              </button>
            )}
            <button className="scan-btn-text" onClick={handleSkipLocation}>
              {t('skipLocation')}
            </button>
          </div>
        </div>
      )}

      {/* ── EDIT LOCATION ── */}
      {step === 'edit-location' && (
        <div className="scan-step scan-location-edit">
          <h2>{t('editLocation')}</h2>
          
          <div className="scan-location-form">
            <div className="scan-field">
              <label>{t('latitude')}</label>
              <input
                type="number"
                step="0.0001"
                placeholder={t('latitudePlaceholder')}
                value={editLat}
                onChange={(e) => setEditLat(e.target.value)}
                className="scan-location-input"
              />
            </div>
            
            <div className="scan-field">
              <label>{t('longitude')}</label>
              <input
                type="number"
                step="0.0001"
                placeholder={t('longitudePlaceholder')}
                value={editLng}
                onChange={(e) => setEditLng(e.target.value)}
                className="scan-location-input"
              />
            </div>

            <button className="scan-btn-secondary scan-btn-gps" onClick={handleUseCurrentLocation}>
              {t('useMyCurrentLocation')}
            </button>

            <div className="scan-location-edit-actions">
              <button className="scan-btn-primary" onClick={handleSaveEditedLocation}>
                {t('common:save')}
              </button>
              <button className="scan-btn-text" onClick={() => setStep('confirm-location')}>
                {t('common:cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── REVIEW ── */}
      {step === 'review' && (
        <div className="scan-step scan-review">
          <div className="scan-summary">
            <h2>{t('framesCapturedTitle', { count: camera.frames.length })}</h2>
            <p>{sectionName || newSectionName} &middot; {selectedShop?.name || shopId}</p>
            <p className="scan-tap-hint">{t('tapToPreview')}</p>
          </div>

          <div className="scan-frame-strip">
            {camera.frames.map((frame, i) => (
              <img
                key={frame.index}
                src={URL.createObjectURL(frame.blob)}
                alt={t('frameAlt', { number: i + 1 })}
                className="scan-thumb"
                onClick={() => setPreviewIndex(i)}
              />
            ))}
          </div>

          <div className="scan-review-actions">
            <button className="scan-btn-secondary" onClick={handleRetake}>
              {t('retake')}
            </button>
            <button className="scan-btn-primary" onClick={handleUpload} disabled={camera.frames.length === 0}>
              {t('uploadFrames', { count: camera.frames.length })}
            </button>
          </div>

          {/* ── Frame Preview Overlay ── */}
          {previewIndex !== null && previewIndex < camera.frames.length && (
            <FramePreview
              frames={camera.frames}
              currentIndex={previewIndex}
              onNavigate={setPreviewIndex}
              onDelete={(idx) => {
                camera.removeFrame(idx)
                if (camera.frames.length <= 1) {
                  setPreviewIndex(null)
                } else if (idx >= camera.frames.length - 1) {
                  setPreviewIndex(idx - 1)
                }
              }}
              onClose={() => setPreviewIndex(null)}
            />
          )}
        </div>
      )}

      {/* ── UPLOAD / PROGRESS ── */}
      {step === 'upload' && (
        <div className="scan-step scan-upload">
          <StatusView phase={uploader.phase} progress={uploader.progress} scan={uploader.scan} error={uploader.error} />

          {uploader.phase === 'error' && (
            <div className="scan-upload-error-actions">
              <button className="scan-btn-primary" onClick={handleRetryUpload} style={{ marginTop: 24 }}>
                {t('retryUpload')}
              </button>
              <button className="scan-btn-secondary" onClick={handleNewScan} style={{ marginTop: 8 }}>
                {t('newScan')}
              </button>
            </div>
          )}
          {uploader.phase === 'done' && (
            <button className="scan-btn-primary" onClick={handleNewScan} style={{ marginTop: 24 }}>
              {t('newScan')}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ── Frame Preview with swipe ──
function FramePreview({
  frames,
  currentIndex,
  onNavigate,
  onDelete,
  onClose,
  hideDelete = false,
}: {
  frames: { blob: Blob; index: number }[]
  currentIndex: number
  onNavigate: (idx: number) => void
  onDelete: (idx: number) => void
  onClose: () => void
  hideDelete?: boolean
}) {
  const { t } = useTranslation('scan')
  const touchStartX = useRef(0)
  const touchDeltaX = useRef(0)
  const stripRef = useRef<HTMLDivElement>(null)

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
    touchDeltaX.current = 0
  }, [])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current
    // Apply live translate for feedback
    if (stripRef.current) {
      stripRef.current.style.transform = `translateX(${touchDeltaX.current}px)`
      stripRef.current.style.transition = 'none'
    }
  }, [])

  const handleTouchEnd = useCallback(() => {
    const delta = touchDeltaX.current
    const threshold = 50 // minimum px to count as swipe
    if (stripRef.current) {
      stripRef.current.style.transform = ''
      stripRef.current.style.transition = 'transform 0.2s ease'
    }
    if (delta < -threshold && currentIndex < frames.length - 1) {
      onNavigate(currentIndex + 1) // swipe left → next
    } else if (delta > threshold && currentIndex > 0) {
      onNavigate(currentIndex - 1) // swipe right → prev
    }
    touchDeltaX.current = 0
  }, [currentIndex, frames.length, onNavigate])

  return (
    <div className="scan-preview-overlay" onClick={onClose}>
      <div className="scan-preview-content" onClick={(e) => e.stopPropagation()}>
        <div className="scan-preview-header">
          <span className="scan-preview-counter">
            {t('frameCounter', { current: currentIndex + 1, total: frames.length })}
          </span>
          <button className="scan-preview-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div
          className="scan-preview-image-wrap"
          ref={stripRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {currentIndex > 0 && (
            <button className="scan-preview-nav scan-preview-prev" onClick={() => onNavigate(currentIndex - 1)}>
              ‹
            </button>
          )}
          <img
            src={URL.createObjectURL(frames[currentIndex].blob)}
            alt={t('frameAlt', { number: currentIndex + 1 })}
            className="scan-preview-img"
            draggable={false}
          />
          {currentIndex < frames.length - 1 && (
            <button className="scan-preview-nav scan-preview-next" onClick={() => onNavigate(currentIndex + 1)}>
              ›
            </button>
          )}
        </div>

        {!hideDelete && (
          <div className="scan-preview-actions">
            <button className="scan-btn-delete" onClick={() => onDelete(currentIndex)}>
              {t('deleteFrame')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Status sub-component ──
function StatusView({
  phase,
  progress,
  scan,
  error,
}: {
  phase: string
  progress: { total: number; uploaded: number; failed: number; percent: number }
  scan: { id: string; status: ScanStatus; processingTime?: string; datasetId?: string } | null
  error: string | null
}) {
  const { t } = useTranslation('scan')

  if (error) {
    return (
      <div className="scan-status scan-status-error">
        <div className="scan-status-icon">✗</div>
        <h2>{t('common:error')}</h2>
        <p>{error}</p>
      </div>
    )
  }

  if (phase === 'creating') {
    return (
      <div className="scan-status">
        <div className="scan-spinner" />
        <h2>{t('creatingScanSession')}</h2>
      </div>
    )
  }

  if (phase === 'uploading') {
    return (
      <div className="scan-status">
        <div className="scan-progress-bar">
          <div className="scan-progress-fill" style={{ width: `${progress.percent}%` }} />
        </div>
        <h2>{t('uploadingFrames')}</h2>
        <p>
          {t('uploadProgress', { uploaded: progress.uploaded, total: progress.total })}
          {progress.failed > 0 && <span className="scan-failed"> ({t('uploadFailedCount', { count: progress.failed })})</span>}
        </p>
      </div>
    )
  }

  if (phase === 'completing') {
    return (
      <div className="scan-status">
        <div className="scan-spinner" />
        <h2>{t('finalizingUpload')}</h2>
      </div>
    )
  }

  if (phase === 'polling' && scan) {
    return (
      <div className="scan-status">
        <div className="scan-spinner" />
        <h2>{t('processingWithColmap')}</h2>
        <p>{t('status', { status: scan.status })}</p>
        <p className="scan-hint">{t('processingHint')}</p>
      </div>
    )
  }

  if (phase === 'done' && scan) {
    const isReady = scan.status === 'ready'
    return (
      <div className={`scan-status ${isReady ? 'scan-status-success' : 'scan-status-error'}`}>
        <div className="scan-status-icon">{isReady ? '✓' : '✗'}</div>
        <h2>{isReady ? t('scanComplete') : t('processingFailed')}</h2>
        {scan.processingTime && <p>{t('processingTime', { time: scan.processingTime })}</p>}
        <p className="scan-scan-id">{t('scanId', { id: scan.id })}</p>
        {isReady && scan.datasetId && (
          <a
            href={`/?shopid=${encodeURIComponent(scan.datasetId)}`}
            className="scan-btn-view-shop"
          >
            {t('viewShop')}
          </a>
        )}
      </div>
    )
  }

  return null
}
